import type { Ref } from 'react'
import React, { forwardRef, useContext } from 'react'
import { css, cx } from '@linaria/core'
import { sanitizeProps, useForwardRef } from '@fs/zion-frontend-friends'
import TextAndIconButton from '../TextAndIconButton'
import {
  colorCss,
  lowCss,
  highCss,
  grayHighCss,
  grayLowCss,
  highNightCss,
  lightHighCss,
  lightHighNightCss,
  grayLightHighNightCss,
  grayLightHighCss,
  grayHighNightCss,
} from '../colorStyles'
import { lowEmphasisCss, mediumEmphasisCss, highEmphasisCss } from '../emphasisStyles'
import { useTheme } from '../../theme'
import { WhiteThemeContext } from '../../theme/white-theme'
import colorThemes from '../../theme/themeSubsets'

import useSquishyButton from '../useSquishyButton'
import type { BillboardButtonProps } from '../types'
import { textBcss } from '../../typography/typographyStyles'

const buttonCss = css`
  display: flex;
  padding: 13px 20px;
  max-width: 100%;
  text-transform: uppercase;
  height: 44px;
  //This is to prevent the button from shrinking vertically when placed in a flex container
  min-height: 44px;

  // This is to line up the baseline of anchor tags with the baseline of buttons and text elements
  a& {
    vertical-align: -16px;
  }
`

const lowEmphasisButtonCss = css`
  padding: 13px 12px;
  margin: 0 -12px;
  // This calculation should use 24px, but an extra pixel prevents always showing the ellipsis in Safari 14 and older
  // Revisit this when we only support iOS 15+
  max-width: calc(100% + 25px); // Need to account for the negative margins
`

/**
 * ### _A larger format version of the Button Component_
 *
 * A BillboardButton **must only be used** for pages that act as introductory experiences
 * to the user. Here are some examples:
 *
 * - Logged-out home pages
 * - Discovery experiences and activities
 * - Marketing pages
 */
const BillboardButton = forwardRef(
  (
    {
      emphasis = 'high',
      color: inputColor,
      fullWidth = false,
      children,
      disabled = false,
      ...props
    }: BillboardButtonProps,
    forwardedRef
  ) => {
    const ref = useForwardRef<HTMLElement>(forwardedRef as Ref<HTMLElement>)
    const buttonScale = useSquishyButton(ref)
    const theme = useTheme()
    const isWhite = useContext(WhiteThemeContext)
    const color = inputColor ?? (isWhite && ['low', 'medium'].includes(emphasis) ? 'gray' : 'blue')
    const classNames = [buttonCss, colorCss, textBcss, colorThemes[color]]
    switch (emphasis) {
      case 'low':
        classNames.push(lowEmphasisCss, lowEmphasisButtonCss)
        if (color === 'gray') {
          classNames.push(grayLowCss)
        } else {
          classNames.push(lowCss)
        }
        break
      case 'medium':
        classNames.push(mediumEmphasisCss)
        if (color === 'gray') {
          classNames.push(grayLowCss)
        } else {
          classNames.push(lowCss)
        }
        break
      case 'lightHigh':
        classNames.push(highEmphasisCss)
        if (color === 'gray') {
          classNames.push(theme === 'night' ? grayLightHighNightCss : grayLightHighCss)
        } else {
          classNames.push(theme === 'night' ? lightHighNightCss : lightHighCss)
        }
        break
      case 'high': // Fall through, this is default
      default:
        classNames.push(highEmphasisCss)
        if (color === 'gray') {
          classNames.push(theme === 'night' ? grayHighNightCss : grayHighCss)
        } else {
          classNames.push(theme === 'night' ? highNightCss : highCss)
        }
    }

    return (
      <TextAndIconButton
        {...sanitizeProps(props)}
        disabled={disabled}
        fullWidth={fullWidth && emphasis !== 'low'}
        iconSize="sm"
        className={cx(...classNames)}
        style={{ '--button-scale': buttonScale }}
        ref={ref}
      >
        {children}
      </TextAndIconButton>
    )
  }
)

export default BillboardButton
